import { ActionSheetButton, useIonActionSheet } from '@ionic/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterBackButtonCallback } from '../../components/header/back-button';
import { useIsKioskModeEnabled } from '../../kiosk/kiosk';
import { runInBackground } from '../../util/async';
import { useIsViewing } from '../../util/hooks';
import { useDirectConnectUserGateContext } from '../gate/direct-connect-user-gate';
import { useDirectConnectGoBack } from '../helper/direct-connect-helper';

const defaultCallback = async () => true;

const useDirectConnectBackButtonListener = (): void => {
  const { i18n } = useTranslation();

  const { user, switchUser } = useDirectConnectUserGateContext();
  const [present] = useIonActionSheet();

  const isViewing = useIsViewing();
  const goBack = useDirectConnectGoBack();

  const isKioskModeEnabled = useIsKioskModeEnabled();

  const backButtonCallback = useCallback(async () => {
    let message: string | undefined;

    if (user) {
      message = i18n.t('common:direct_connect_logged_in_as', {
        name: user.name,
      });
    }

    return new Promise<boolean>((resolve) => {
      const buttons: ActionSheetButton[] = [];

      buttons.push({
        text: i18n.t('common:direct_connect_switch_user'),
        handler: () => {
          runInBackground(async () => {
            switchUser();
            resolve(false);
          });
        },
      });

      if (!isKioskModeEnabled) {
        buttons.push({
          text: i18n.t('common:disconnect'),
          role: 'destructive',
          handler: () => {
            runInBackground(async () => {
              goBack();
              resolve(false);
            });
          },
        });
      }

      buttons.push({
        text: i18n.t('common:cancel'),
        role: 'cancel',
        handler: () => {
          resolve(false);
        },
      });

      present(buttons, message);
    });
  }, [user, i18n, present, switchUser, goBack, isKioskModeEnabled]);

  // register back button callback so we can disconnect from wifi
  useRegisterBackButtonCallback(
    isViewing ? backButtonCallback : defaultCallback
  );
};

export { useDirectConnectBackButtonListener };
